import * as Sentry from "@sentry/react"
import { RotateCw } from "lucide-react"
import { Component, type ErrorInfo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/primitives"
import { useAppOutdated } from "@/hooks/use-app-outdated"
import { reloadIfOutdated } from "@/lib/app-version"
import { cn } from "@/lib/utils"

type ReloadBoundaryVariant = "page" | "block"

type ReloadBoundaryProps = {
  children: ReactNode
  /** page — на весь экран вместо приложения; block — внутри колонки, которую охраняет. */
  variant?: ReloadBoundaryVariant
}

type ReloadBoundaryState = { failed: boolean }

/** Вместо пустого экрана после ошибки рендера предлагает перезагрузить страницу. */
export class ReloadBoundary extends Component<ReloadBoundaryProps, ReloadBoundaryState> {
  state: ReloadBoundaryState = { failed: false }

  static getDerivedStateFromError(): ReloadBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack ?? "" } } })
    // Чаще всего так падает вкладка со старым JS после деплоя — свежая сборка лечится перезагрузкой.
    void reloadIfOutdated()
  }

  render() {
    if (this.state.failed) return <ReloadPrompt variant={this.props.variant ?? "page"} />
    return this.props.children
  }
}

function ReloadPrompt({ variant }: { variant: ReloadBoundaryVariant }) {
  const { t } = useTranslation("layout")
  const outdated = useAppOutdated()
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 text-center",
        variant === "page" ? "min-h-[100dvh] bg-ui-bg" : "h-full min-h-0 flex-1 py-20",
      )}
    >
      <p className="max-w-sm text-sm/6 text-balance text-ui-text-secondary">
        {t(outdated ? "appReload.hintOutdated" : "appReload.hint")}
      </p>
      <ReloadButton />
    </div>
  )
}

export function ReloadButton({ className }: { className?: string }) {
  const { t } = useTranslation("layout")
  return (
    <Button type="button" size="sm" className={className} onClick={() => window.location.reload()}>
      <RotateCw className="h-3.5 w-3.5" aria-hidden />
      {t("appReload.action")}
    </Button>
  )
}
